import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import DateIcon from '../../assets/images/date.png';
import TimeIcon from '../../assets/images/time.png';
import ClockIcon from '../../assets/images/clock.png';
import { useForm } from 'react-hook-form';
import {
  useGetAppointments,
  useGetBusinessHours,
  useRejectAppointment,
  useUpdateAppointmentNote,
  useRescheduleAppointment,
} from '../../hooks';
import ReactModal from 'react-modal';
import CalenderViewAppointmentForm from '../../components/templates/calendar-view-appointment-form/calendar-view-appointment-form';
import { Spinner } from '../../components/atoms';
import { AddClientForm } from '../scheduler-clients/add-client-form';
import { Navigate } from 'react-big-calendar';
import LeftIcon from '../../assets/images/left-arw.png';
import RightIcon from '../../assets/images/right-arw.png';
import close from '../../assets/images/close-crl.svg';
import reschedule from '../../assets/images/reschedule.svg';
import { yupResolver } from '@hookform/resolvers/yup';
import { appointmentRejectionSchema } from '../../constants';
import {
  getAppointmentCalendarEventStyle,
  showError,
  showSuccess,
} from '../../utils';
import { RescheduleAppointmentForm } from '../../components/templates';
import { UpdateAppointmentNote } from './update-appointment-note-form';
const localizer = momentLocalizer(moment);

// Create Calendar with drag and drop support
const DnDCalendar = withDragAndDrop(Calendar);

function MyCalendar({
  isFirstModalOpen,
  setFirstModalOpen,
  openFirstModal,
  refreshTrigger,
}) {
  const views = {
    month: true,
    week: true,
    day: true,
    agenda: false,
  };
  const [filter, setFilter] = useState({
    filter: {
      startTimeGteq: moment().startOf('month').toISOString(),
      startTimeLteq: moment().endOf('month').toISOString(),
    },
    pagination: {},
  });

  const { data, refetch, loading } = useGetAppointments(filter, true);
  const [events, setEvents] = useState([]);
  const [currentView, setCurrentView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [clickedDate, setClickedDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [activeDate, setActiveDate] = useState(new Date());
  const [newClientData, setNewClientData] = useState('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [editNoteModalOpen, setEditNoteModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [originalEvent, setOriginalEvent] = useState(null);
  const [dragDropModalOpen, setDragDropModalOpen] = useState(false);
  const handleRejectAppointment = useRejectAppointment();
  const handleUpdateAppointmentNote = useUpdateAppointmentNote();
  const handleRescheduleAppointment = useRescheduleAppointment();

  const businessHoursDateRange = useMemo(() => {
    if (currentView === 'day') {
      const d = moment(currentDate);
      return {
        fromDate: d.format('YYYY-MM-DD'),
        toDate: d.format('YYYY-MM-DD'),
      };
    }

    const start = moment(currentDate).startOf('week');
    const end = moment(currentDate).endOf('week');
    return {
      fromDate: start.format('YYYY-MM-DD'),
      toDate: end.format('YYYY-MM-DD'),
    };
  }, [currentDate, currentView]);

  const { data: businessHoursData } = useGetBusinessHours(
    businessHoursDateRange
  );

  const calendarTimeBounds = useMemo(() => {
    const toMinutes = (hhmm) => {
      if (!hhmm) return null;
      const m = String(hhmm)
        .trim()
        .match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return null;
      const h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
      return h * 60 + min;
    };

    const defaultMinMinutes = 8 * 60;
    const defaultMaxMinutes = 20 * 60;

    const histories = businessHoursData?.businessHoursHistories || [];
    const allRanges = histories.flatMap((d) => d.histories || []);

    const starts = allRanges
      .map((h) => toMinutes(h.startTime))
      .filter((v) => Number.isFinite(v));
    const ends = allRanges
      .map((h) => toMinutes(h.endTime))
      .filter((v) => Number.isFinite(v));

    const earliest = starts.length ? Math.min(...starts) : defaultMinMinutes;
    const latest = ends.length ? Math.max(...ends) : defaultMaxMinutes;

    const minMinutes = Math.max(0, earliest - 60);
    const maxMinutes = Math.min(24 * 60, latest + 60);

    const makeTime = (mins) => {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return new Date(1970, 0, 1, h, m, 0, 0);
    };

    return {
      min: makeTime(minMinutes),
      max: makeTime(maxMinutes),
      scrollToTime: makeTime(
        Math.min(Math.max(minMinutes, defaultMinMinutes), maxMinutes)
      ),
    };
  }, [businessHoursData]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(appointmentRejectionSchema),
  });

  const openCancelModal = (value) => {
    reset();
    setSelectedAppointment(value);
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    reset();
    setSelectedAppointment(null);
    setCancelModalOpen(false);
  };

  const openRescheduleModal = (value) => {
    setSelectedAppointment(value);
    setRescheduleModalOpen(true);
  };

  const closeRescheduleModal = () => {
    setSelectedAppointment(null);
    setRescheduleModalOpen(false);
  };

  const openEditNoteModal = (value) => {
    setSelectedAppointment(value);
    setEditNoteModalOpen(true);
  };

  const closeEditNoteModal = () => {
    setSelectedAppointment(null);
    setEditNoteModalOpen(false);
  };

  const onCancelSubmit = async (formData) => {
    setIsLoading(true);
    const payload = {
      appointmentUid: selectedAppointment?.uid,
      ...formData,
    };
    const response = await handleRejectAppointment(payload);
    if (response?.data?.rejectAppointment) {
      showSuccess('Appointment cenceled successfully');
      closeCancelModal();
      setShowEventPopup(false);
      refetch();
      setIsLoading(false);
    } else {
      setIsLoading(false);
      showError(response);
    }
  };
  const updateFilter = useCallback((view, date) => {
    let start, end;
    if (view === 'month') {
      start = moment(date).startOf('month');
      end = moment(date).endOf('month');
    } else if (view === 'week') {
      start = moment(date).startOf('week');
      end = moment(date).endOf('week');
    } else if (view === 'day') {
      start = moment(date).startOf('day');
      end = moment(date).endOf('day');
    }
    setFilter((prevFilter) => ({
      ...prevFilter,
      filter: {
        ...prevFilter.filter,
        startTimeGteq: start.toISOString(),
        startTimeLteq: end.toISOString(),
      },
    }));
  }, []);

  useEffect(() => {
    updateFilter(currentView, currentDate);
  }, [currentView, currentDate, updateFilter]);

  useEffect(() => {
    refetch();
  }, [filter, refetch]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      refetch();
    }
  }, [refreshTrigger, refetch]);

  useEffect(() => {
    if (data?.appointments?.nodes) {
      const events = data.appointments.nodes.map((appointment) => {
        console.log('appointment === ', appointment);
        const start = new Date(appointment.startTime);
        const end = new Date(
          start.getTime() + appointment.sellerService.durationInMinutes * 60000
        );
        return {
          uid: appointment.uid,
          startTime: appointment.startTime,
          sellerService: appointment.sellerService,
          service: appointment.sellerService.name,
          title: `${
            appointment?.familyMember?.formalName ||
            appointment.buyer.formalName
          } - ${appointment.sellerService.name}`,
          start: start,
          end: end,
          buyerName:
            appointment?.familyMember?.formalName ||
            appointment.buyer.formalName,
          status: appointment.status,
          durationInMinutes: appointment.sellerService.durationInMinutes,
          buyerNote: appointment.buyerNote,
          sellerNote: appointment.sellerNote,
        };
      });
      setEvents(events);
    }
  }, [data]);

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleNavigate = (date, view) => {
    if (showEventPopup) {
      return;
    }
    setCurrentDate(date);
    setCurrentView(view);
  };

  const [isSecondModalOpen, setSecondModalOpen] = useState(false);

  const closeFirstModal = () => {
    setNewClientData('');
    setFirstModalOpen(false);
  };
  const openSecondModal = () => {
    // Keep first modal open, overlay second modal on top
    setSecondModalOpen(true);
  };
  const closeSecondModal = () => setSecondModalOpen(false);

  // custom event
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [showEventPopup, setShowEventPopup] = useState(false);

  const handleEventClick = (event) => {
    const clickedDate = moment(event.start).format('MMMM D, YYYY');
    setShowEventPopup(true);
    setClickedDate(clickedDate);
    setSelectedDateEvents([event]);
  };

  const handleSelectSlot = ({ start, action }) => {
    console.log('Select slot:', { start, action });
    const today = new Date();
    today.setHours(0, 0, 0);
    if (action === 'click' || action === 'touch' || action === 'select') {
      if (start < today) {
        return;
      }
      setActiveDate(start);
      openFirstModal();
    }
  };

  // Handle drag and drop of appointments
  const handleEventDrop = ({ event, start, end }) => {
    console.log('Event dropped:', { event, start, end });

    // Store original event for potential revert
    setOriginalEvent({ ...event });

    // Optimistically update event position so it visually moves
    setEvents((prevEvents) =>
      prevEvents.map((e) => (e.uid === event.uid ? { ...e, start, end } : e))
    );

    setDraggedEvent({ event, start, end });
    // Close any existing reschedule modal first
    setRescheduleModalOpen(false);
    // Then open the drag-drop confirm modal
    setTimeout(() => setDragDropModalOpen(true), 100);
  };

  // Cancel drag-drop and revert to original position
  const handleDragDropCancel = () => {
    if (originalEvent) {
      // Revert event back to original position
      setEvents((prevEvents) =>
        prevEvents.map((e) =>
          e.uid === originalEvent.uid
            ? { ...e, start: originalEvent.start, end: originalEvent.end }
            : e
        )
      );
    }
    setDragDropModalOpen(false);
    setDraggedEvent(null);
    setOriginalEvent(null);
  };

  // Confirm reschedule after drag and drop
  const handleRescheduleConfirm = async () => {
    if (!draggedEvent) return;

    setIsLoading(true);
    const response = await handleRescheduleAppointment({
      appointmentUid: draggedEvent.event.uid,
      input: {
        startTime: draggedEvent.start.toISOString(),
      },
      allowUnbookableTime: true,
    });

    if (response?.data?.rescheduleAppointment) {
      showSuccess('Appointment rescheduled successfully');
      setDragDropModalOpen(false);
      setDraggedEvent(null);
      setOriginalEvent(null);
      refetch();
    } else {
      // Revert on error
      if (originalEvent) {
        setEvents((prevEvents) =>
          prevEvents.map((e) =>
            e.uid === originalEvent.uid
              ? { ...e, start: originalEvent.start, end: originalEvent.end }
              : e
          )
        );
      }
      showError(response);
      setDragDropModalOpen(false);
      setDraggedEvent(null);
      setOriginalEvent(null);
    }
    setIsLoading(false);
  };

  const eventPropGetter = useCallback((event, start, end, isSelected) => {
    const eventStyle = getAppointmentCalendarEventStyle(event, isSelected);

    // Return style object in the format react-big-calendar expects
    return {
      style: {
        ...eventStyle,
        cursor: 'grab',
      },
    };
  }, []);

  const onShowMore = useCallback((events, date) => {
    const clickedDate = moment(date).format('YYYY-MM-DD');
    const filteredEvents = events.filter(
      (ev) => moment(ev.start).format('YYYY-MM-DD') === clickedDate
    );

    setSelectedDateEvents(filteredEvents);
    setShowEventPopup(true);
    setClickedDate(moment(date).format('MMMM D, YYYY'));
  }, []);

  const customToolbar = ({ label, onNavigate, view, onView }) => {
    return (
      <div className="rbc-toolbar flex justify-between">
        <span className="rbc-btn-group md:mb-0 mb-3">
          <button
            type="button"
            onClick={() => onNavigate(Navigate.TODAY)}
            className=" border-0 bg-transparent text-xs font-semibold"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => onNavigate(Navigate.PREVIOUS)}
            className=" border-0 bg-transparent"
          >
            <img src={LeftIcon} alt="Previous" />
          </button>
          <span className="rbc-toolbar-label font-semibold md:text-base text-xs">
            {label}
          </span>
          <button
            type="button"
            onClick={() => onNavigate(Navigate.NEXT)}
            className=" border-0 bg-transparent"
          >
            <img src={RightIcon} alt="Next" />
          </button>
        </span>

        <span className="rbc-btn-group">
          <button
            type="button"
            className={
              view === 'month'
                ? 'rbc-active border-0 text-xs font-bold min-w-118'
                : ' border-0 text-xs font-bold min-w-118'
            }
            onClick={() => onView('month')}
          >
            Month
          </button>
          <button
            type="button"
            className={
              view === 'week'
                ? 'rbc-active border-0 text-xs font-bold min-w-118'
                : 'border-0 text-xs font-bold  border-l border-r border-purple-bdr min-w-118'
            }
            onClick={() => onView('week')}
          >
            Week
          </button>
          <button
            type="button"
            className={
              view === 'day'
                ? 'rbc-active border-0 text-xs font-bold min-w-118'
                : 'border-0 text-xs font-bold border-l border-purple-bdr min-w-118'
            }
            onClick={() => onView('day')}
          >
            Day
          </button>
        </span>
      </div>
    );
  };

  const formatToHoursMin = (value) => {
    const minutes = value % 60;
    const hours = Math.floor(value / 60);
    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes} min`;
  };

  const desktopFormats = {
    weekdayFormat: (date, culture, localizer) =>
      localizer.format(date, 'dddd', culture),

    dayHeaderFormat: (date, culture, localizer) =>
      localizer.format(date, 'MMMM D, YYYY', culture),

    dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'MMM DD', culture)} – ${localizer.format(
        end,
        'MMM DD',
        culture
      )}`,

    dayFormat: (date, culture, localizer) =>
      localizer.format(date, 'dddd DD', culture),

    dateFormat: (date, culture, localizer) =>
      localizer.format(date, 'D', culture),

    eventTimeRangeFormat: () => null,
    eventTimeRangeStartFormat: () => null,
    eventTimeRangeEndFormat: () => null,
  };

  const mobileFormats = {
    weekdayFormat: (date, culture, localizer) =>
      localizer.format(date, 'ddd', culture),

    dayHeaderFormat: (date, culture, localizer) =>
      localizer.format(date, 'MMM D', culture),

    dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'MMM DD', culture)} – ${localizer.format(
        end,
        'MMM DD',
        culture
      )}`,

    dayFormat: (date, culture, localizer) =>
      localizer.format(date, 'ddd', culture),

    dateFormat: (date, culture, localizer) =>
      localizer.format(date, 'D', culture),

    eventTimeRangeFormat: () => null,
    eventTimeRangeStartFormat: () => null,
    eventTimeRangeEndFormat: () => null,
  };

  const getFormats = () => {
    const isMobile = window.innerWidth <= 768;
    return isMobile ? mobileFormats : desktopFormats;
  };

  const customTitle = (event) => {
    const startTime = moment(event.start).format('hh:mm A');
    const endTime = moment(event.end).format('hh:mm A');

    return (
      <div className="custom-event">
        <p className="event-details">{`${event.buyerName} - ${event.service}, ${startTime} - ${endTime}`}</p>
      </div>
    );
  };

  const customTooltip = (event) => {
    const startTime = moment(event.start).format('hh:mm A');
    const endTime = moment(event.end).format('hh:mm A');
    return `${event.buyerName} - ${event.service}, ${startTime} - ${endTime}`;
  };

  return (
    <>
      {(isLoading || loading) && <Spinner isBlock={false} />}
      <section className="pt-4 appointment-calender">
        <div style={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
          <DnDCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            defaultView="month"
            views={views}
            dayLayoutAlgorithm="no-overlap"
            min={calendarTimeBounds.min}
            max={calendarTimeBounds.max}
            scrollToTime={calendarTimeBounds.scrollToTime}
            components={{
              toolbar: customToolbar,
            }}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            eventPropGetter={eventPropGetter}
            onSelectEvent={handleEventClick}
            onSelectSlot={handleSelectSlot}
            selectable
            onEventDrop={handleEventDrop}
            resizableAccessor={() => false} // Disable resizing
            onShowMore={onShowMore}
            formats={getFormats()}
            titleAccessor={(event) => customTitle(event)}
            tooltipAccessor={(event) => customTooltip(event)}
            onDrillDown={(date, view) => {
              if (view !== 'day') {
                setCurrentDate(date);
                setCurrentView(view);
              }
            }}
          />
          {/* Book An Appointment */}
          <ReactModal
            isOpen={isFirstModalOpen}
            onRequestClose={closeFirstModal}
            contentLabel="Example Modal"
            className={'md:w-120 w-5/6 bg-white-main'}
            style={{
              overlay: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 31,
                backdropFilter: 'blur(2px)',
              },
              content: {
                position: 'absolute',
                top: '50%',
                left: '50%',
                maxHeight: '99vh',
                overflow: 'auto',
                transform: 'translate(-50%, -50%)',
                border: '1px solid #ccc',
                backgroundColor: '#fff',

                WebkitOverflowScrolling: 'touch',
                borderRadius: '4px',
                outline: 'none',
                padding: '20px',
              },
            }}
          >
            <div className="flex justify-between items-center md:mb-6 mb-1">
              <h2 className=" font-semibold md:text-2xl text-base">
                Book an appointment
              </h2>
              <span
                className="absolute right-4 top-2 font-semibold cursor-pointer"
                onClick={closeFirstModal}
              >
                &#x2715;
              </span>
            </div>
            <CalenderViewAppointmentForm
              activeDate={activeDate}
              closeFirstModal={closeFirstModal}
              setIsLoading={setIsLoading}
              refetch={refetch}
              openSecondModal={openSecondModal}
              newClientData={newClientData}
              businessHoursData={businessHoursData}
            />
          </ReactModal>
          {isSecondModalOpen && (
            <div className="modal-overlay" style={{ zIndex: 40 }}>
              <div className="modal md:w-120">
                <div className="modal-content book-appointment">
                  <span
                    className="close absolute right-3 top-2 font-semibold cursor-pointer"
                    onClick={closeSecondModal}
                  >
                    &#x2715;
                  </span>
                  <AddClientForm
                    setIsLoading={setIsLoading}
                    closeAddNewClientModal={closeSecondModal}
                    refetch={refetch}
                    pageView="calendar-view"
                    openFirstModal={openFirstModal}
                    setNewClientData={setNewClientData}
                  />
                </div>
              </div>
            </div>
          )}
          {showEventPopup && (
            <div className="modal-overlay">
              <div className="modal md:w-150 max-h-80 overflow-y-auto">
                <div className="modal-content">
                  <span
                    className="close absolute right-3 top-2 font-semibold cursor-pointer"
                    onClick={() => setShowEventPopup(false)}
                  >
                    &#x2715;
                  </span>
                  <div className="flex items-center justify-between gap-12 pt-3">
                    <h2 className="font-semibold md:text-2xl text-sm">
                      Appointment Details{' '}
                    </h2>
                    <div className="font-semibold text-gray-middle md:text-base text-xs flex items-center gap-3">
                      <img src={DateIcon} alt="icon" />
                      <p>{clickedDate}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-2 mb-4">
                    <p className="md:text-base text-sm text-gray-dark">
                      Total Bookings :
                    </p>
                    <p className="text-base text-primary font-semibold">
                      {selectedDateEvents.length}
                    </p>
                  </div>
                  {selectedDateEvents.map((event, index) => (
                    <div className="grid mb-4" key={index}>
                      {event?.status !== 'rejected' &&
                        event?.status !== 'completed' &&
                        event?.status !== 'canceled' && (
                          <div className="flex gap-3 justify-end mb-2">
                            {new Date(event?.start) - new Date() >= 0 && (
                              <img
                                src={reschedule}
                                alt="logo"
                                className="cursor-pointer"
                                onClick={() => openRescheduleModal(event)}
                              />
                            )}
                            <img
                              src={close}
                              alt="logo"
                              className="cursor-pointer"
                              onClick={() => openCancelModal(event)}
                            />
                            {event?.sellerNote?.uid && (
                              <span
                                className="cursor-pointer"
                                onClick={() => openEditNoteModal(event)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="21"
                                  viewBox="0 0 20 21"
                                  fill="none"
                                >
                                  <g opacity="0.5">
                                    <path
                                      d="M15 9.25V16.4375C15 16.6427 14.9596 16.8459 14.8811 17.0354C14.8025 17.225 14.6874 17.3973 14.5424 17.5424C14.3973 17.6874 14.225 17.8025 14.0354 17.8811C13.8459 17.9596 13.6427 18 13.4375 18H4.0625C3.6481 18 3.25067 17.8354 2.95765 17.5424C2.66462 17.2493 2.5 16.8519 2.5 16.4375V7.0625C2.5 6.6481 2.66462 6.25067 2.95765 5.95765C3.25067 5.66462 3.6481 5.5 4.0625 5.5H10.6047"
                                      stroke="#585858"
                                      strokeWidth="1.3"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M15.5286 3.94486L15.5287 3.94483C15.6133 3.86042 15.728 3.81302 15.8475 3.81302C15.9671 3.81302 16.0817 3.86042 16.1664 3.94483L16.1666 3.9451L16.5553 4.33573L16.5555 4.33593C16.6392 4.42047 16.6861 4.5346 16.6861 4.65353C16.6861 4.77246 16.6392 4.88658 16.5555 4.97113L16.5552 4.97144L9.51622 12.0237L15.5286 3.94486ZM15.5286 3.94486L8.47714 10.9838M15.5286 3.94486L8.03644 12.1479M8.03644 12.1479L8.0365 12.1479L8.36195 11.1785C8.3832 11.1049 8.42283 11.0379 8.47714 10.9838M8.03644 12.1479L8.03542 12.1513M8.03644 12.1479L8.03542 12.1513M8.47714 10.9838L8.5477 11.0547M8.47714 10.9838L8.47706 10.9839L8.5477 11.0547M8.5477 11.0547C8.50495 11.0973 8.47387 11.1501 8.45747 11.2082L8.5477 11.0547ZM8.03542 12.1513C8.02249 12.1949 8.02158 12.2412 8.03277 12.2853C8.04396 12.3294 8.06685 12.3696 8.09902 12.4018M8.03542 12.1513L8.09902 12.4018M8.34958 12.4655L8.34956 12.4654C8.30595 12.4783 8.25965 12.4793 8.21555 12.4681C8.17145 12.4569 8.13119 12.434 8.09902 12.4018M8.34958 12.4655L8.09902 12.4018C8.09902 12.4018 8.09902 12.4018 8.09902 12.4018M8.34958 12.4655L8.35299 12.4643M8.34958 12.4655L8.35299 12.4643M8.35299 12.4643L9.32152 12.1389M8.35299 12.4643L9.32152 12.1389M9.32152 12.1389C9.39509 12.1177 9.46207 12.078 9.51614 12.0238L9.32152 12.1389ZM16.5057 2.96819L16.5055 2.96839C16.4282 3.04574 16.3848 3.15062 16.3848 3.25997C16.3848 3.36933 16.4282 3.47421 16.5055 3.55156L16.5056 3.55164L16.9483 3.9936L17.0192 3.92306L16.9485 3.99383L16.9485 3.99375C16.9868 4.03219 17.0323 4.0627 17.0824 4.08352L17.1207 3.99118L17.0824 4.08352C17.1325 4.10437 17.1863 4.1151 17.2407 4.1151C17.295 4.1151 17.3488 4.10437 17.399 4.08352C17.4491 4.0627 17.4946 4.0322 17.5329 3.99377C17.5329 3.99371 17.533 3.99366 17.533 3.9936L18.0038 3.5252L18.0039 3.52508C18.2772 3.25219 18.3063 2.80394 18.0404 2.51278C17.9742 2.44007 17.894 2.38152 17.8046 2.34069C17.7151 2.29986 17.6183 2.27758 17.52 2.27521C17.4217 2.27284 17.324 2.29042 17.2327 2.3269C17.1414 2.36337 17.0583 2.418 16.9986 2.48549L16.9986 2.48551C16.9844 2.50157 16.9707 2.5183 16.9576 2.53564L16.9575 2.53561L16.9562 2.53755L16.9565 2.53776C16.926 2.57794 16.8918 2.61506 16.8543 2.64877L16.8543 2.64874L16.8527 2.65032L16.853 2.65056L16.5057 2.96819Z"
                                      fill="#585858"
                                      stroke="#585858"
                                      strokeWidth="0.2"
                                    />
                                  </g>
                                </svg>
                              </span>
                            )}
                          </div>
                        )}
                      <div className=" grid grid-cols-3 gap-2 cal-data-shadow border border-gray-apBg px-3 py-3 rounded-lg">
                        <div>
                          <p className="md:text-sm text-xs font-medium text-gray-middle">
                            Name
                          </p>
                          <p className="md:text-sm text-xs text-gray-dark font-semibold">
                            {event.buyerName}
                          </p>
                          <div className="flex gap-3 justify-start items-center mt-3">
                            <div
                              className={`inline-flex rounded-full items-center py-0.5 whitespace-nowrap ${
                                event?.status === 'accepted'
                                  ? 'bg-green-lame'
                                  : event?.status === 'rejected' ||
                                    event?.status === 'canceled'
                                  ? 'bg-red-Verylight'
                                  : event?.status === 'completed'
                                  ? 'bg-gray-completed'
                                  : 'bg-blue-chipBg'
                              } px-3`}
                            >
                              <span
                                className={`text-xs capitalize ${
                                  event?.status === 'accepted' ||
                                  event?.status === 'completed'
                                    ? '.text-gray-middle'
                                    : event?.status === 'rejected' ||
                                      event?.status === 'canceled'
                                    ? 'text-red-drk'
                                    : 'text-primary'
                                } font-medium`}
                              >
                                {event?.status === 'accepted' ||
                                event?.status === 'completed'
                                  ? event?.status
                                  : event?.status === 'rejected' ||
                                    event?.status === 'canceled'
                                  ? event?.status
                                  : 'Rescheduled'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <p className="md:text-sm text-xs font-medium text-gray-middle">
                            Service
                          </p>
                          <p className="md:text-sm text-xs text-gray-dark font-semibold">
                            {event.service}
                          </p>
                        </div>
                        <div>
                          <div className="flex gap-3 justify-start items-center">
                            <img src={TimeIcon} alt="icon" />
                            <p className="text-gray-middle font-medium md:text-sm text-xs">
                              {moment(event.start).format('hh:mm A')}
                            </p>
                          </div>
                          <div className="flex gap-3 justify-start items-start">
                            <img
                              src={ClockIcon}
                              alt="icon"
                              className=" mt-0.5"
                            />
                            <p className="text-gray-middle font-medium md:text-sm text-xs">
                              Duration :{' '}
                              {formatToHoursMin(event.durationInMinutes)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {(event?.buyerNote?.content ||
                        event?.sellerNote?.content) && (
                        <div className="cal-data-shadow border border-gray-apBg px-3 py-3 rounded-lg mt-2">
                          <p className="md:text-sm text-xs font-medium text-gray-middle mb-2">
                            Notes
                          </p>

                          {event?.buyerNote?.content && (
                            <div className="border-b border-gray-light pb-2 mb-2">
                              <p className="text-xs text-gray-middle font-medium">
                                Buyer Notes
                              </p>
                              <p className="text-xs text-gray-dark mt-1 break-all">
                                {event?.buyerNote?.content}
                              </p>
                            </div>
                          )}

                          {event?.sellerNote?.content && (
                            <div>
                              <p className="text-xs text-gray-middle font-medium">
                                SFO Notes
                              </p>
                              <p className="text-xs text-gray-dark mt-1 break-all">
                                {event?.sellerNote?.content}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <ReactModal
            isOpen={cancelModalOpen}
            onRequestClose={closeCancelModal}
            contentLabel="Cancel Appointment"
            className={'md:w-120 w-5/6 bg-white-main'}
            style={{
              overlay: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 31,
                backdropFilter: 'blur(2px)',
              },
              content: {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                borderRadius: '4px',
                outline: 'none',
                padding: '20px',
              },
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className=" font-semibold md:text-2xl text-base">
                Cancel Appointment
              </h2>
              <span
                className="absolute right-4 top-2 font-semibold cursor-pointer"
                onClick={closeCancelModal}
              >
                &#x2715;
              </span>
            </div>
            <form onSubmit={handleSubmit(onCancelSubmit)}>
              <div className="grid w-full relative">
                <textarea
                  rows="4"
                  placeholder="Add reason"
                  className="w-full text-xs font-medium rounded border border-purple-bdr console-input p-2"
                  {...register('comment')}
                  onInput={(event) => {
                    const maxLength = 200;
                    const value = event.target.value;
                    if (value.length > maxLength) {
                      event.target.value = value.slice(0, maxLength);
                    }
                  }}
                />
                {errors && errors?.comment && (
                  <p className="text-red-drk md:text-r0.6875 text-r0.625">
                    {errors?.comment?.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2  gap-2  md:gap-8 mt-6">
                <button
                  onClick={closeCancelModal}
                  type="button"
                  className="flex-1 md:flex-none h-10 w-full  border border-gray-dark bg-very-light text-gray-dark text-xs hover:text-gray-middle hover:border-very-dark py-2 text-center font-bold rounded-md"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="flex-1 md:flex-none h-10 w-full border bg-red-cover text-white-main  text-xs hover:bg-red-danger-hover py-2 text-center font-bold rounded-md"
                >
                  Cancel
                </button>
              </div>
            </form>
          </ReactModal>

          <ReactModal
            isOpen={rescheduleModalOpen}
            onRequestClose={closeRescheduleModal}
            contentLabel="Reschedule Appointment"
            className={'md:w-120 w-5/6 bg-white-main'}
            style={{
              overlay: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 31,
                backdropFilter: 'blur(2px)',
              },
              content: {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                WebkitOverflowScrolling: 'touch',
                borderRadius: '4px',
                outline: 'none',
                padding: '20px',
              },
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className=" font-semibold md:text-2xl text-base">
                Reschedule Appointment
              </h2>
              <span
                className="absolute right-4 top-2 font-semibold cursor-pointer"
                onClick={closeRescheduleModal}
              >
                &#x2715;
              </span>
            </div>
            <RescheduleAppointmentForm
              selectedAppointment={selectedAppointment}
              closeRescheduleModal={closeRescheduleModal}
              details={true}
              setAppointmentDetails={(updated) => {
                const start = new Date(updated.startTime);
                const end = new Date(
                  start.getTime() +
                    updated.sellerService.durationInMinutes * 60000
                );

                const updatedEvent = {
                  ...selectedAppointment,
                  ...updated,
                  start,
                  end,
                  startTime: updated.startTime,
                  service: updated.sellerService.name,
                  sellerService: updated.sellerService,
                };

                setSelectedAppointment(updatedEvent);
                setEvents((prev) =>
                  prev.map((e) =>
                    e.uid === updatedEvent.uid ? updatedEvent : e
                  )
                );
                setSelectedDateEvents((prev) =>
                  prev.map((e) =>
                    e.uid === updatedEvent.uid ? updatedEvent : e
                  )
                );

                refetch();
              }}
              setIsLoading={setIsLoading}
              refetch={refetch}
            />
          </ReactModal>

          <ReactModal
            isOpen={editNoteModalOpen}
            onRequestClose={closeEditNoteModal}
            contentLabel="Edit SFO Notes"
            className={'md:w-120 w-5/6 bg-white-main'}
            style={{
              overlay: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 31,
                backdropFilter: 'blur(2px)',
              },
              content: {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                borderRadius: '4px',
                outline: 'none',
                padding: '20px',
              },
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className=" font-semibold md:text-2xl text-base">
                Edit SFO Notes
              </h2>
              <span
                className="absolute right-4 top-2 font-semibold cursor-pointer"
                onClick={closeEditNoteModal}
              >
                &#x2715;
              </span>
            </div>
            <UpdateAppointmentNote
              setIsEditMode={() => setEditNoteModalOpen(false)}
              appointmentDetails={selectedAppointment}
              handleUpdateAppointmentNote={handleUpdateAppointmentNote}
              setIsLoading={setIsLoading}
              setAppointmentDetails={(updated) => {
                setSelectedAppointment(updated);
                setSelectedDateEvents((prev) =>
                  prev.map((e) =>
                    e.uid === updated?.uid ? { ...e, ...updated } : e
                  )
                );
                setEvents((prev) =>
                  prev.map((e) =>
                    e.uid === updated?.uid ? { ...e, ...updated } : e
                  )
                );
              }}
            />
          </ReactModal>

          {/* Reschedule confirmation modal for drag and drop */}
          <ReactModal
            isOpen={dragDropModalOpen}
            onRequestClose={handleDragDropCancel}
            contentLabel="Confirm Reschedule"
            className={'md:w-120 w-5/6 bg-white-main'}
            style={{
              overlay: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 51, // Highest z-index for drag-drop confirm modal
                backdropFilter: 'blur(2px)',
              },
              content: {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                borderRadius: '4px',
                outline: 'none',
                padding: '20px',
              },
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-semibold md:text-2xl text-base">
                Confirm Reschedule
              </h2>
              <span
                className="absolute right-4 top-2 font-semibold cursor-pointer"
                onClick={handleDragDropCancel}
              >
                &#x2715;
              </span>
            </div>
            {draggedEvent && (
              <div className="mb-4">
                <p className="text-sm text-gray-dark mb-2">
                  <strong>Appointment:</strong> {draggedEvent.event.buyerName} -{' '}
                  {draggedEvent.event.service}
                </p>
                <p className="text-sm text-gray-dark mb-2">
                  <strong>From:</strong>{' '}
                  {moment(draggedEvent.event.start).format(
                    'MMM D, YYYY hh:mm A'
                  )}
                </p>
                <p className="text-sm text-gray-dark mb-4">
                  <strong>To:</strong>{' '}
                  {moment(draggedEvent.start).format('MMM D, YYYY hh:mm A')}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 md:gap-8 mt-6">
              <button
                type="button"
                onClick={handleDragDropCancel}
                className="flex-1 md:flex-none h-10 w-full border border-gray-dark bg-very-light text-gray-dark text-xs hover:text-gray-middle hover:border-very-dark py-2 text-center font-bold rounded-md"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRescheduleConfirm}
                disabled={isLoading}
                className="flex-1 md:flex-none h-10 w-full border border-primary bg-primary text-white-main text-xs hover:bg-blue-hover py-2 text-center font-bold rounded-md disabled:opacity-50"
              >
                {isLoading ? 'Rescheduling...' : 'Confirm'}
              </button>
            </div>
          </ReactModal>
        </div>
      </section>
    </>
  );
}

export default MyCalendar;
